import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { GridOptions, GridRow } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { RequisitionService, RequisitionSimplifiedAction, RequisitionSimplifiedActionParams, RequisitionSimplifiedGet, RequisitionSimplifiedLabourItem, RequisitionSimplifiedStatusItem, RequisitionSimplifiedStockplaceFromItem, RequisitionSimplifiedStockplaceToItem } from '@app/services/requisition.service';

@Component({
    selector: 'tt-requisition-simplified-filters',
    templateUrl: './requisition-simplified-filters.component.html',
    styleUrls: ['./requisition-simplified-filters.component.css'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequisitionSimplifiedFiltersComponent implements OnInit {
    @Input() public get?: RequisitionSimplifiedGet;

    @Input() public requisitionDelGrid?: GridOptions<RequisitionSimplifiedDelRow>;

    @Output() public submit = new EventEmitter<void>();

    public loadingAction?: RequisitionSimplifiedAction;

    public stockplacesFrom: RequisitionSimplifiedStockplaceFromItem[] = [];

    public stockplacesTo: RequisitionSimplifiedStockplaceToItem[] = [];

    public labours: RequisitionSimplifiedLabourItem[] = [];

    public statuses: RequisitionSimplifiedStatusItem[] = [];

    constructor(
        private requisition: RequisitionService,
        private modal: ModalService,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
    ) {}

    public async send() {
        if (!this.requisitionDelGrid?.gridfunc) return;

        const rows = this.getSelectedRequisitionDelParams();
        const selectedIdsText = rows.map((item) => item.requisitiondel_keyno).join(', ');
        const confirmed = await this.modal.openConfirmDialog({ type: 'warning', title: 'confirm_title', translateTitle: true, message: `${await this.translate.translate('send')} ID: ${selectedIdsText}` });

        if (!confirmed) return;

        let processedMessage = 'IDs processed successfully: ';
        let hasError = false;

        try {
            this.setLoadingAction('send');
            this.requisitionDelGrid.gridfunc.gridProgress(true);

            for (const row of rows) {
                const result = await this.requisition.send(row);

                if ('' + result.errorcode !== '0') {
                    this.requisitionDelGrid.gridfunc.gridProgress(false);
                    this.setLoadingAction();
                    await this.modal.show({
                        type: 'danger',
                        title: 'Varsel',
                        message: `${processedMessage}\nIDs with error: ${row.requisitiondel_keyno}\nError code: ${result.errorcode}\nError message: ${result.errormessage}`,
                    });
                    return;
                }

                processedMessage += `${row.requisitiondel_keyno}, `;
            }
        } catch (error) {
            console.error(error);
            hasError = true;
        }

        if (hasError) {
            this.requisitionDelGrid.gridfunc.gridProgress(false);
            this.setLoadingAction();
            return;
        }

        try {
            await this.requisitionDelGrid.gridfunc.read();
        } catch (error) {
            console.error(error);
            this.requisitionDelGrid.gridfunc.gridProgress(false);
            return;
        } finally {
            this.setLoadingAction();
        }

        await this.modal.show({ type: 'success', title: 'Varsel', message: 'Done' });
    }

    public async receive() {
        if (!this.requisitionDelGrid?.gridfunc) return;

        const rows = this.getSelectedRequisitionDelParams();
        const selectedIdsText = rows.map((item) => item.requisitiondel_keyno).join(', ');
        const confirmed = await this.modal.openConfirmDialog({
            type: 'warning',
            title: 'confirm_title',
            translateTitle: true,
            message: `${await this.translate.translate('receive')} ID: ${selectedIdsText}`,
        });

        if (!confirmed) return;

        let processedMessage = 'IDs processed successfully: ';
        let hasError = false;

        try {
            this.setLoadingAction('receive');
            this.requisitionDelGrid.gridfunc.gridProgress(true);

            for (const row of rows) {
                const result = await this.requisition.receive(row);

                if ('' + result.errorcode !== '0') {
                    this.requisitionDelGrid.gridfunc.gridProgress(false);
                    this.setLoadingAction();
                    await this.modal.show({
                        type: 'danger',
                        title: 'Varsel',
                        message: `${processedMessage}\nIDs with error: ${row.requisitiondel_keyno}\nError code: ${result.errorcode}\nError message: ${result.errormessage}`,
                    });
                    return;
                }

                processedMessage += `${row.requisitiondel_keyno}, `;
            }
        } catch (error) {
            console.error(error);
            hasError = true;
        }

        if (hasError) {
            this.requisitionDelGrid.gridfunc.gridProgress(false);
            this.setLoadingAction();
            return;
        }

        try {
            await this.requisitionDelGrid.gridfunc.read();
        } catch (error) {
            console.error(error);
            this.requisitionDelGrid.gridfunc.gridProgress(false);
            return;
        } finally {
            this.setLoadingAction();
        }

        await this.modal.show({ type: 'success', title: 'Varsel', message: 'Done' });
    }

    public async undoSend() {
        if (!this.requisitionDelGrid?.gridfunc) return;

        const rows = this.getSelectedRequisitionDelParams();
        const selectedIdsText = rows.map((item) => item.requisitiondel_keyno).join(', ');
        const confirmed = await this.modal.openConfirmDialog({
            type: 'warning',
            title: 'confirm_title',
            translateTitle: true,
            message: `${await this.translate.translate('undo_send')} ID: ${selectedIdsText}`,
        });

        if (!confirmed) return;

        let processedMessage = 'IDs processed successfully: ';
        let hasError = false;

        try {
            this.setLoadingAction('undo_send');
            this.requisitionDelGrid.gridfunc.gridProgress(true);

            for (const row of rows) {
                const result = await this.requisition.undoSend(row);

                if ('' + result.errorcode !== '0') {
                    this.requisitionDelGrid.gridfunc.gridProgress(false);
                    this.setLoadingAction();
                    await this.modal.show({
                        type: 'danger',
                        title: 'Varsel',
                        message: `${processedMessage}\nIDs with error: ${row.requisitiondel_keyno}\nError code: ${result.errorcode}\nError message: ${result.errormessage}`,
                    });
                    return;
                }

                processedMessage += `${row.requisitiondel_keyno}, `;
            }
        } catch (error) {
            console.error(error);
            hasError = true;
        }

        if (hasError) {
            this.requisitionDelGrid.gridfunc.gridProgress(false);
            this.setLoadingAction();
            return;
        }

        try {
            await this.requisitionDelGrid.gridfunc.read();
        } catch (error) {
            console.error(error);
            this.requisitionDelGrid.gridfunc.gridProgress(false);
            return;
        } finally {
            this.setLoadingAction();
        }

        await this.modal.show({ type: 'success', title: 'Varsel', message: 'Done' });
    }

    public async undoReceive() {
        if (!this.requisitionDelGrid?.gridfunc) return;

        const rows = this.getSelectedRequisitionDelParams();
        const selectedIdsText = rows.map((item) => item.requisitiondel_keyno).join(', ');
        const confirmed = await this.modal.openConfirmDialog({
            type: 'warning',
            title: 'confirm_title',
            translateTitle: true,
            message: `${await this.translate.translate('undo_receive')} ID: ${selectedIdsText}`,
        });

        if (!confirmed) return;

        let processedMessage = 'IDs processed successfully: ';
        let hasError = false;

        try {
            this.setLoadingAction('undo_receive');
            this.requisitionDelGrid.gridfunc.gridProgress(true);

            for (const row of rows) {
                const result = await this.requisition.undoReceive(row);

                if ('' + result.errorcode !== '0') {
                    this.requisitionDelGrid.gridfunc.gridProgress(false);
                    this.setLoadingAction();
                    await this.modal.show({
                        type: 'danger',
                        title: 'Varsel',
                        message: `${processedMessage}\nIDs with error: ${row.requisitiondel_keyno}\nError code: ${result.errorcode}\nError message: ${result.errormessage}`,
                    });
                    return;
                }

                processedMessage += `${row.requisitiondel_keyno}, `;
            }
        } catch (error) {
            console.error(error);
            hasError = true;
        }

        if (hasError) {
            this.requisitionDelGrid.gridfunc.gridProgress(false);
            this.setLoadingAction();
            return;
        }

        try {
            await this.requisitionDelGrid.gridfunc.read();
        } catch (error) {
            console.error(error);
            this.requisitionDelGrid.gridfunc.gridProgress(false);
            return;
        } finally {
            this.setLoadingAction();
        }

        await this.modal.show({ type: 'success', title: 'Varsel', message: 'Done' });
    }

    private getSelectedRequisitionDelParams(): RequisitionSimplifiedActionParams[] {
        return (this.requisitionDelGrid?.gridfunc?.getIsSelectedRows() ?? [])
            .map((row) => row.requisitiondel_keyno)
            .filter((requisitiondelKeyno): requisitiondelKeyno is string | number => requisitiondelKeyno !== undefined && requisitiondelKeyno !== null && `${requisitiondelKeyno}` !== '')
            .map((requisitiondelKeyno) => ({ requisitiondel_keyno: requisitiondelKeyno }));
    }

    private async loadFilterOptions() {
        const [stockplacesFrom, stockplacesTo, labours, statuses] = await Promise.all([this.requisition.getStockplaceFrom(), this.requisition.getStockplaceTo(), this.requisition.getLabour(), this.requisition.getStatus()]);

        this.stockplacesFrom = stockplacesFrom;
        this.stockplacesTo = stockplacesTo;
        this.labours = labours;
        this.statuses = statuses;
        this.cdr.markForCheck();
    }

    private setLoadingAction(action?: RequisitionSimplifiedAction) {
        this.loadingAction = action;
        this.cdr.markForCheck();
    }

    ngOnInit(): void {
        void this.loadFilterOptions();
    }
}

type RequisitionSimplifiedDelRow = GridRow & {
    requisitiondel_keyno?: string | number;
    is_selected?: boolean;
};
